import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trophy, Medal, Sparkles, Eye, Maximize, Minimize, FileDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import GamificationAchievementsModal from '../components/GamificationAchievementsModal';
import { gamificationAPI, publicGamificationAPI } from '../services/api';

import './AdminGamificationRankingPage.css';

const AUTO_REFRESH_INTERVAL_MS = 60_000;

// Cada tela da TV (mês ou acumulado) mostra todos os membros, com rolagem manual. As barras largam
// em momentos aleatórios dentro de BAR_MAX_START_DELAY_MS e levam BAR_FILL_MS para encher; a tela
// troca após BAR_HOLD_MS de pausa (ciclo total de VIEW_DURATION_MS).
export const BAR_MAX_START_DELAY_MS = 10_000;
export const BAR_FILL_MS = 10_000;
export const BAR_HOLD_MS = 5_000;
export const VIEW_DURATION_MS = BAR_MAX_START_DELAY_MS + BAR_FILL_MS + BAR_HOLD_MS;

export const RankingBar = ({ points, leaderPoints }) => {
  const [delay] = useState(() => Math.random() * (BAR_MAX_START_DELAY_MS / 1000));
  const [progress, setProgress] = useState(0);
  const percentage = leaderPoints > 0 ? Math.min(100, Math.max(0, points / leaderPoints * 100)) : 0;

  useEffect(() => {
    const motionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let frameId;
    const startAnimation = () => {
      cancelAnimationFrame(frameId);
      if (motionPreference?.matches) {
        setProgress(1);
        return;
      }
      setProgress(0);
      const startedAt = performance.now() + delay * 1000;
      const tick = (now) => {
        const elapsed = Math.max(0, now - startedAt);
        const next = Math.min(elapsed / BAR_FILL_MS, 1);
        setProgress(next);
        if (next < 1) frameId = requestAnimationFrame(tick);
      };
      frameId = requestAnimationFrame(tick);
    };
    startAnimation();
    motionPreference?.addEventListener('change', startAnimation);
    return () => {
      cancelAnimationFrame(frameId);
      motionPreference?.removeEventListener('change', startAnimation);
    };
  }, [delay]);

  return (
    <div className="ranking-bar-track" aria-hidden="true">
      <div className="ranking-bar-target" style={{ width: `${percentage}%` }}>
        <div className="ranking-bar-fill" data-delay={delay} style={{ width: `${progress * 100}%` }}>
          <span className="ranking-bar-score">
            {Math.floor(Math.max(0, points) * progress).toLocaleString('pt-BR')}
          </span>
        </div>
      </div>
    </div>
  );
};

const currentMonth = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit' })
    .format(new Date())
    .slice(0, 7);

const shiftMonth = (value, delta) => {
  const [year, month] = value.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1 + delta, 1)).toISOString().slice(0, 7);
};

const formatMonthLabel = (value) => {
  const [year, month] = value.split('-').map(Number);
  const label = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const openMonthPicker = (event) => {
  try {
    event.currentTarget.showPicker?.();
  } catch (error) {
    // showPicker pode lançar fora de gesto do usuário; o ícone nativo continua funcionando.
  }
};

const previousMonth = () => {
  return shiftMonth(currentMonth(), -1);
};

const formatTime = (date) =>
  date
    ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '--:--:--';

const medalColor = (position) => {
  if (position === 0) return 'text-amber-300';
  if (position === 1) return 'text-slate-300';
  if (position === 2) return 'text-orange-400';
  return 'text-slate-500';
};

const LoadingRows = () => (
  <div className="space-y-2">
    {[...Array(6)].map((_, index) => (
      <div key={`ranking-skeleton-${index}`} className="grid grid-cols-12 gap-3 items-center rounded-lg border border-gray-800 bg-gray-900 px-4 py-3">
        <Skeleton className="h-4 col-span-1 bg-gray-800" />
        <Skeleton className="h-4 col-span-5 bg-gray-800" />
        <Skeleton className="h-4 col-span-3 bg-gray-800" />
        <Skeleton className="h-4 col-span-3 bg-gray-800" />
      </div>
    ))}
  </div>
);

// Depois que as barras encheram e a pausa passou, chama onCycleEnd (a rolagem é manual).
const useViewCycle = (onCycleEnd, active) => {
  const onCycleEndRef = useRef(onCycleEnd);
  onCycleEndRef.current = onCycleEnd;

  useEffect(() => {
    if (!active) return undefined;
    const timeoutId = setTimeout(() => onCycleEndRef.current?.(), VIEW_DURATION_MS);
    return () => clearTimeout(timeoutId);
  }, [active]);
};

const RankingTable = ({ rows, loading, emptyLabel, onViewUser, fullscreen, showBars, leaderPoints, onCycleEnd }) => {
  useViewCycle(onCycleEnd, Boolean(onCycleEnd) && !loading);

  if (loading) return <LoadingRows />;

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-gray-800 bg-gray-950 px-4 py-6 text-center text-gray-300">
        {emptyLabel}
      </div>
    );
  }

  const textSize = fullscreen ? 'text-lg' : 'text-sm';

  return (
    <div className={showBars ? 'flex-1 min-h-0 overflow-y-auto' : 'overflow-x-auto'}>
      <table className={`w-full ${showBars ? "" : "min-w-[720px]"} ${textSize}`}>
        <thead>
          <tr className="border-b border-gray-800 text-gray-400 text-left">
            <th className="px-3 py-3 font-medium sticky top-0 z-10 bg-gray-900 w-14">#</th>
            <th className="px-3 py-3 font-medium sticky top-0 z-10 bg-gray-900">Membro</th>
            {showBars && <th className="px-3 py-3 font-medium sticky top-0 z-10 bg-gray-900 w-1/3"><span className="sr-only">Pontuação relativa ao líder</span></th>}
            <th className="px-3 py-3 font-medium sticky top-0 z-10 bg-gray-900">Total</th>
            {!showBars && <th className="px-3 py-3 font-medium sticky top-0 z-10 bg-gray-900">Conquistas</th>}
            <th className="px-3 py-3 font-medium sticky top-0 z-10 bg-gray-900">Qtd.</th>
            {!showBars && <th className="px-3 py-3 font-medium sticky top-0 z-10 bg-gray-900">Evento</th>}
            {!fullscreen && onViewUser && <th className="px-3 py-3 font-medium sticky top-0 z-10 bg-gray-900 w-12" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.user_id || index} className="border-b border-gray-800/80 text-gray-200">
              <td className="px-3 py-3">
                <div className="flex items-center gap-1">
                  {index < 3 ? <Medal size={fullscreen ? 22 : 16} className={medalColor(index)} /> : null}
                  <span className="font-medium text-white">{index + 1}</span>
                </div>
              </td>
              <td className="px-3 py-3 font-medium text-white">{row.full_name}</td>
              {showBars && (
                <td className="px-3 py-3">
                  <RankingBar points={row.total_points || 0} leaderPoints={leaderPoints ?? (rows[0].total_points || 0)} />
                </td>
              )}
              <td className={`px-3 py-3 font-bold text-white tabular-nums ${fullscreen ? 'text-3xl' : 'text-xl'}`}>
                {(row.total_points || 0).toLocaleString('pt-BR')}
              </td>
              {!showBars && <td className="px-3 py-3 text-gray-300">{(row.member_points || 0).toLocaleString('pt-BR')} pts</td>}
              <td className="px-3 py-3 text-gray-300">{row.conquistas || 0}</td>
              {!showBars && (
                <td className="px-3 py-3 text-gray-300">
                  {row.event_points ? `${row.event_points.toLocaleString('pt-BR')} pts` : '—'}
                </td>
              )}
              {!fullscreen && onViewUser && (
                <td className="px-3 py-3">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-neutral-400 hover:text-white"
                    onClick={() => onViewUser(row)}
                    aria-label={`Ver conquistas de ${row.full_name}`}
                  >
                    <Eye size={16} />
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PublicRankingLayout = ({ children }) => (
  <main className="h-screen overflow-hidden flex flex-col bg-background p-4 md:p-8">{children}</main>
);

const AdminGamificationRankingPage = ({ publicView = false }) => {
  const PageLayout = publicView ? PublicRankingLayout : Layout;
  const rankingAPI = publicView ? publicGamificationAPI : gamificationAPI;
  const [month, setMonth] = useState(currentMonth());
  const [view, setView] = useState('month'); // 'month' | 'all_time'
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [allTimeRows, setAllTimeRows] = useState([]);
  const [loadingMonthly, setLoadingMonthly] = useState(true);
  const [loadingAllTime, setLoadingAllTime] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const fullscreenRef = useRef(null);
  const monthRef = useRef(month);
  monthRef.current = month;

  const loadMonthly = useCallback(async (targetMonth, { silent = false } = {}) => {
    if (!silent) setLoadingMonthly(true);
    try {
      const response = await rankingAPI.getMonthlyRanking(targetMonth);
      setMonthlyRows(response.data || []);
      setLastUpdated(new Date());
    } catch (error) {
      if (!silent) toast.error('Não foi possível carregar o ranking do mês.');
    } finally {
      if (!silent) setLoadingMonthly(false);
    }
  }, [rankingAPI]);

  const loadAllTime = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingAllTime(true);
    try {
      const response = await rankingAPI.getAllTimeRanking();
      setAllTimeRows(response.data || []);
      setLastUpdated(new Date());
    } catch (error) {
      if (!silent) toast.error('Não foi possível carregar o ranking acumulado.');
    } finally {
      if (!silent) setLoadingAllTime(false);
    }
  }, [rankingAPI]);

  useEffect(() => {
    loadMonthly(month);
  }, [month, loadMonthly]);

  useEffect(() => {
    loadAllTime();
  }, [loadAllTime]);

  useEffect(() => {
    const intervalId = setInterval(async () => {
      setRefreshing(true);
      await Promise.all([loadMonthly(monthRef.current, { silent: true }), loadAllTime({ silent: true })]);
      setRefreshing(false);
    }, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [loadMonthly, loadAllTime]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === fullscreenRef.current);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const isAutoRotating = publicView || isFullscreen;
  const isMonthView = view === 'month';
  const rows = isMonthView ? monthlyRows : allTimeRows;
  const loading = isMonthView ? loadingMonthly : loadingAllTime;
  const toggleView = useCallback(() => {
    setView(current => current === 'month' ? 'all_time' : 'month');
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (fullscreenRef.current) {
        await fullscreenRef.current.requestFullscreen();
      }
    } catch (error) {
      toast.error('Não foi possível alternar a tela cheia neste navegador.');
    }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [closingMonth, setClosingMonth] = useState(previousMonth());
  const downloadClosingPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await gamificationAPI.downloadClosingPdf(closingMonth);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `fechamento-elite-${closingMonth}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Não foi possível gerar o PDF de fechamento.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleRejected = () => {
    loadMonthly(month);
    loadAllTime();
  };

  const selectView = (nextView) => {
    setView(nextView);
  };

  return (
    <PageLayout>
      <div className={publicView ? 'flex flex-col flex-1 min-h-0 gap-6' : 'space-y-6'} data-testid="admin-gamification-ranking-page">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Ranking de Gamificação</h1>
          {!publicView && <p className="text-gray-400 mt-1">Acompanhe as conquistas registradas pelos membros — pensado para exibição em TV.</p>}
        </div>

        {!publicView && (
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline">
              <a href="/ranking-tv" target="_blank" rel="noopener noreferrer">Abrir URL pública para TV</a>
            </Button>
            <Input
              type="month"
              value={closingMonth}
              max={previousMonth()}
              onChange={(event) => event.target.value && setClosingMonth(event.target.value)}
              className="w-40 bg-gray-950 border-gray-800 text-white [color-scheme:dark] cursor-pointer"
              onClick={openMonthPicker}
              aria-label="Selecionar mês do fechamento em PDF"
            />
            <Button variant="outline" onClick={downloadClosingPdf} disabled={downloadingPdf}>
              <FileDown size={16} className="mr-2" />
              {downloadingPdf ? 'Gerando PDF...' : `Baixar fechamento de ${closingMonth} (PDF)`}
            </Button>
          </div>
        )}

        <div ref={fullscreenRef} className={isFullscreen ? 'bg-background h-screen overflow-hidden p-8 flex flex-col' : publicView ? 'flex flex-col flex-1 min-h-0' : ''}>
          <Card className={`border-gray-800 bg-gray-900 ${isAutoRotating ? 'flex flex-col flex-1 min-h-0' : ''}`}>
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className={`text-white flex flex-wrap items-center gap-2 ${isFullscreen ? 'text-2xl' : ''}`}>
                {isMonthView ? (
                  <Trophy size={isFullscreen ? 28 : 18} className="text-amber-300" />
                ) : (
                  <Sparkles size={isFullscreen ? 28 : 18} className="text-indigo-300" />
                )}
                {isMonthView ? 'Ranking do mês' : 'Ranking acumulado geral'}
                <span className="ml-2 flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-950/40 px-2 py-0.5 text-xs font-normal text-emerald-300">
                  <span className="relative flex h-2 w-2">
                    <span
                      className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 ${
                        refreshing ? 'animate-ping opacity-75' : 'opacity-0'
                      }`}
                    />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  </span>
                  Ao vivo
                </span>
                {lastUpdated && (
                  <span className="text-xs font-normal text-gray-500">
                    Atualizado às {formatTime(lastUpdated)}
                  </span>
                )}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex rounded-md border border-gray-800 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => selectView('month')}
                    className={`px-3 py-2 text-sm transition-colors ${
                      isMonthView ? 'bg-indigo-600 text-white' : 'bg-gray-950 text-gray-400 hover:text-white'
                    }`}
                  >
                    Mês
                  </button>
                  <button
                    type="button"
                    onClick={() => selectView('all_time')}
                    className={`px-3 py-2 text-sm transition-colors ${
                      !isMonthView ? 'bg-indigo-600 text-white' : 'bg-gray-950 text-gray-400 hover:text-white'
                    }`}
                  >
                    Total
                  </button>
                </div>
                {isMonthView && (
                  <div className="flex items-center rounded-md border border-gray-800 overflow-hidden" data-testid="ranking-month-selector">
                    <button
                      type="button"
                      onClick={() => setMonth(current => shiftMonth(current, -1))}
                      className="px-3 py-2 bg-gray-950 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      aria-label="Mês anterior"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <span className="min-w-[10rem] px-3 py-2 text-center text-sm text-white bg-gray-950" aria-live="polite">
                      {formatMonthLabel(month)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setMonth(current => shiftMonth(current, 1))}
                      disabled={month >= currentMonth()}
                      className="px-3 py-2 bg-gray-950 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-30 disabled:hover:text-gray-300"
                      aria-label="Próximo mês"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                )}
                <Button
                  size="icon"
                  variant="outline"
                  className="border-gray-800 text-gray-300 hover:text-white"
                  onClick={toggleFullscreen}
                  aria-label={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                  title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                >
                  {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
                </Button>
              </div>
            </CardHeader>
            <CardContent className={isAutoRotating ? 'flex flex-col flex-1 min-h-0' : ''}>
              <RankingTable
                key={isMonthView ? `month-${month}` : 'all_time'}
                rows={rows}
                leaderPoints={rows[0]?.total_points || 0}
                loading={loading}
                emptyLabel={isMonthView ? 'Nenhuma conquista pontuada neste mês.' : 'Nenhuma conquista pontuada ainda.'}
                onViewUser={publicView ? undefined : setSelectedUser}
                fullscreen={isFullscreen}
                showBars={publicView || isFullscreen}
                onCycleEnd={isAutoRotating ? toggleView : undefined}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {!publicView && <GamificationAchievementsModal
        open={!!selectedUser}
        onOpenChange={(open) => !open && setSelectedUser(null)}
        userId={selectedUser?.user_id}
        userName={selectedUser?.full_name}
        month={month}
        onRejected={handleRejected}
      />}
    </PageLayout>
  );
};

export default AdminGamificationRankingPage;
